// Client layer (TS-2.5.03). One product's section on the Product view: name, labeled owners, a
// status rollup strip and its project list - or, for the Unassigned bucket (product: null), the
// designed empty/near-total state D-266 expects for v1.
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import type { ProductGroup } from "./lib/groupProjectsByProduct";
import { statusRollup } from "./lib/statusRollup";
import { readableTextColor } from "./lib/contrast";
import { strings } from "../../strings";

export interface ProductGroupCardProps {
  readonly group: ProductGroup;
}

const S = strings.products;

function formatPercent(value: number | null): string {
  return value === null ? "—" : `${Math.round(value)}%`;
}

export function ProductGroupCard({ group }: ProductGroupCardProps) {
  const { product, isUnknown, projects } = group;
  const isUnassigned = product === null;
  const rollup = statusRollup(projects);
  // A retired product still shows its own section (its projects have not moved), badged so a
  // reader is not left wondering why a product on this screen offers nobody a way to choose it.
  const isRetired = !isUnassigned && (!product.isActive || isUnknown);

  return (
    <section className="eg-product-card" aria-label={isUnassigned ? S.unassigned.heading : product.name}>
      <header className="eg-product-card__header">
        <h2>
          {isUnassigned ? S.unassigned.heading : product.name}
          {isRetired && <span className="eg-product-card__retired-badge">{S.retiredBadge}</span>}
        </h2>
        <span className="eg-product-card__count">
          {projects.length} {S.projectCount}
        </span>
      </header>

      {!isUnassigned && (
        <dl className="eg-product-card__owners">
          <dt>{S.technologyOwner}</dt>
          <dd>{product.technologyOwner || S.noOwner}</dd>
          <dt>{S.businessOwner}</dt>
          <dd>{product.businessOwner || S.noOwner}</dd>
        </dl>
      )}

      {/* The v1 "likely every project starts here" state (D-266) is designed, not an error: a
          fixed hint rather than a generic empty-state placeholder. */}
      {isUnassigned && <p className="eg-product-card__hint">{S.unassigned.hint}</p>}

      {rollup.length > 0 && (
        <ul className="eg-product-card__rollup" aria-label={S.aria.statusRollup}>
          {rollup.map((entry) => (
            <li
              key={entry.canonical}
              className="eg-product-card__rollup-chip"
              style={
                {
                  "--eg-chip-color": entry.colorHex,
                  "--eg-chip-ink": readableTextColor(entry.colorHex),
                } as CSSProperties
              }
            >
              {entry.canonical} ({entry.count})
            </li>
          ))}
        </ul>
      )}

      {projects.length > 0 && (
        <table className="eg-product-card__projects">
          <caption>{isUnassigned ? S.unassigned.heading : product.name}</caption>
          <thead>
            <tr>
              <th scope="col">{S.projectColumns.name}</th>
              <th scope="col">{S.projectColumns.lead}</th>
              <th scope="col">{S.projectColumns.status}</th>
              <th scope="col">{S.projectColumns.percentComplete}</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => {
              // D-280: a project's OWN business owner is shown only when it OVERRIDES the
              // product's (non-null and different) - an inheriting row (null) or one that merely
              // repeats the product's own value shows nothing extra here, since the product's own
              // owners line above the table already states the shared value once.
              const override =
                !isUnassigned && project.businessOwner && project.businessOwner !== product.businessOwner
                  ? project.businessOwner
                  : null;

              return (
                <tr key={project.projectKey}>
                  <th scope="row">
                    <Link to={`/projects/${project.projectKey}`}>{project.name}</Link>
                    {override && <div className="eg-product-card__owner-override">{S.businessOwnerOverride(override)}</div>}
                  </th>
                  <td>{project.owner || S.noOwner}</td>
                  <td>
                    <span
                      className="eg-product-card__status-pill"
                      style={
                        {
                          "--eg-chip-color": project.status.colorHex,
                          "--eg-chip-ink": readableTextColor(project.status.colorHex),
                        } as CSSProperties
                      }
                    >
                      {project.status.canonical}
                    </span>
                  </td>
                  <td>{formatPercent(project.percentComplete)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
